import React, {useState, useEffect} from "react";
import { ArrowRight as IconRight, Clock, Heart as IconHeart, QuestionCircle as IconCircleQuestion, Star as IconStar } from "flowbite-react-icons/outline";
import {BarrierInterface} from "../interfaces";
import {useTranslation} from "react-i18next";
import {getLanguage} from "../../../lang/helpers";
import CheckBox from "../../../components/Boxes/CheckBox";
import AttentionBox from "../../../components/Boxes/AttentionBox";
import CheckCircleIcon from "../../../components/Icons/CheckCircleIcon";
import { Check as IconCheck } from "flowbite-react-icons/outline";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../../components/Modal";
import CustomButton from "../../../components/CustomButton";


interface Props {
  stepNumber: number;
  barriers: Array<BarrierInterface>;
  previousActivated?: string;
  save: Function;
  disabled: boolean;
  done: boolean;
  open: boolean;
  readOnly: boolean;
}

interface SelectBarrier {
  text: string;
  type: string;
  selected: boolean;
  custom: boolean;
}

const ClientBarriers = (props: Props)=> {
  const {t, i18n} = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const [showModal, setShowModal] = useState(props.open);
  const [step, setStep] = useState(props.barriers.length > 0 ? 2 : 1);
  const wide = window.innerWidth > 1100;
  const [barriers, setBarriers] = useState<Array<SelectBarrier>>([]);
  const [noBarriers, setNoBarriers] = useState(false);
  const [tooLong, setTooLong] = useState(false);
  const [customText, setCustomText] = useState("");
  const previousActivated = props.previousActivated ? new Date(props.previousActivated) : null;
  const now = new Date();

  // Barrier labels are language-specific, so rebuild the list when the language changes.
  useEffect(() => {
    populateBarriers();
  }, [lang]);

  useEffect(() => {
    setShowModal(props.open);
  }, [props.open])

  const populateBarriers = () => {
    const _barriers: Array<SelectBarrier> = [];

    _barriers.push({
      text: lang === "sv" ? "Jag vet inte var jag ska börja" : "I don't know where to start",
      type: "doIt",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Jag saknar kunskapen för att lyckas" : "I lack the knowledge to succeed",
      type: "doIt",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Jag är osäker på om jag har det som krävs" : "I'm not sure I have what it takes",
      type: "doIt",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Jag vet inte vad som är ett bra resultat" : "I don't know what a good result looks like",
      type: "doIt",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Jag har försökt tidigare utan att lyckas" : "I've tried before without succeeding",
      type: "doIt",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Jag känner mig inte redo än" : "I don't feel ready yet",
      type: "doIt",
      selected: false,
      custom: false
    });

    // Can it work
    _barriers.push({
      text: lang === "sv" ? "Jag har inte tillräckligt med tid just nu" : "I don't have enough time right now",
      type: "work",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Jag saknar rätt förutsättningar" : "I lack the right conditions",
      type: "work",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Andra prioriteringar tar över" : "Other priorities keep taking over",
      type: "work",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Det saknas utrymme i min vardag" : "There's no room for it in my everyday life",
      type: "work",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Jag saknar resurser" : "I lack the resources",
      type: "work",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Jag saknar tillräckligt med stöd från andra" : "I don't have enough support from others",
      type: "work",
      selected: false,
      custom: false
    });

    // Do I want to
    _barriers.push({
      text: lang === "sv" ? "Jag känner mig inte så motiverad" : "I don’t feel motivated enough",
      type: "want",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Jag är rädd att misslyckas" : "I'm afraid of failing",
      type: "want",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Jag tappar lätt fokus" : "I easily lose focus",
      type: "want",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Jag tenderar att skjuta upp saker" : "I tend to put things off",
      type: "want",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Det känns inte relevant just nu" : "It doesn't feel relevant right now",
      type: "want",
      selected: false,
      custom: false
    });

    _barriers.push({
      text: lang === "sv" ? "Det känns överväldigande" : "It feels overwhelming",
      type: "want",
      selected: false,
      custom: false
    });

    for (const barrier of props.barriers) {
      if (barrier.custom) {
        _barriers.push({
          text: barrier.text,
          custom: true,
          selected: true,
          type: "own"
        })
      } else {
        for (const _barrier of _barriers) {
          if (_barrier.text === barrier.text) {
            _barrier.selected = true;
          }
        }
      }
    }

    setBarriers(_barriers);
  }

  const checkBarrier = (text: string) => {
    const _barriers = [...barriers];
    for (let _barrier of _barriers) {
      if (_barrier.text === text) {
        _barrier.selected = !_barrier.selected;
      }
    }
    setBarriers(_barriers);
  }

  const barrierChecked = () => {
    for (const barrier of barriers) {
      if (barrier.selected) {
        return true;
      }
    }
    return false;
  }

  const updateCustom = (txt: string) => {
    if (txt.length < 41) {
      setCustomText(txt);
      setTooLong(false);
    } else {
      setTooLong(true);
    }
  }

  const addBarrier = () => {
    const _barriers = [...barriers];
    _barriers.push({
      text: customText,
      type: "own",
      custom: true,
      selected: true,
    });
    setBarriers(_barriers);
    setCustomText("");
  }

  const customDisabled = () => {
    if (props.readOnly) return true;
    if (tooLong) return true;
    if (noBarriers) return true;
    if (customText.length < 3) return true;
    return false;
  }


  const renderStep = () => {
    if (step === 1) {
      let selectCls = "mt-2 p-4 flex items-center gap-x-2 rounded-md bg-indigo-800 hover:bg-indigo-700 cursor-pointer";
      let noBarrierCls = "mt-8 p-4 flex items-center gap-x-2 rounded-md bg-indigo-800 hover:bg-indigo-700 cursor-pointer";
      if (noBarriers) {
        selectCls += " opacity-50 pointer-events-none";
      }
      if (barrierChecked()) {
        noBarrierCls += " opacity-50 pointer-events-none";
      }

      return (
        <>
          <div className="z-body1">
            {lang === "sv" ? "Välj de hinder som står mellan dig och ditt mål just nu." :
              "Select the barriers standing between you and your goal right now."}
          </div>

          <div className="mt-4 flex items-center gap-x-2">
            <IconStar className="h-8 w-8 text-neutral-400" aria-hidden="true" />
            <div>
              <div className="z-title1">{lang === "sv" ? "Kan jag?" : "Can I do it?"}</div>
              <div
                className="z-body1">{lang === "sv" ? "Kunskap, färdighet, självförtroende" : "Knowledge, skills, confidence"}</div>
            </div>
          </div>

          {
            barriers.filter(b => b.type === "doIt").map((barrier, index) => {
              return (
                <div key={barrier.type + "_" + index}
                     className={selectCls}
                     onClick={() => checkBarrier(barrier.text)}>
                  <CheckBox checked={barrier.selected}/>
                  <div className="z-title2">{barrier.text}</div>
                </div>
              )
            })
          }

          <div className="mt-8 flex items-center gap-x-2">
            <Clock className="h-8 w-8"/>
            <div>
              <div className="z-title1">{lang === "sv" ? "Går det?" : "Can it work?"}</div>
              <div
                className="z-body1">{lang === "sv" ? "Tid, kontext, stöd" : "Time, space, support"}</div>
            </div>
          </div>

          {
            barriers.filter(b => b.type === "work").map((barrier, index) => {
              return (
                <div key={barrier.type + "_" + index}
                     className={selectCls}
                     onClick={() => checkBarrier(barrier.text)}>
                  <CheckBox checked={barrier.selected}/>
                  <div className="z-title2">{barrier.text}</div>
                </div>
              )
            })
          }

          <div className="mt-8 flex items-center gap-x-2">
            <IconHeart className="h-8 w-8 text-gray-400" aria-hidden="true" />
            <div>
              <div className="z-title1">{lang === "sv" ? "Vill jag?" : "Do I want to?"}</div>
              <div
                className="z-body1">{lang === "sv" ? "Motivation, fokus, rädsla" : "Motivation, focus, fears"}</div>
            </div>
          </div>

          {
            barriers.filter(b => b.type === "want").map((barrier, index) => {
              return (
                <div key={barrier.type + "_" + index}
                     className={selectCls}
                     onClick={() => checkBarrier(barrier.text)}>
                  <CheckBox checked={barrier.selected}/>
                  <div className="z-title2">{barrier.text}</div>
                </div>
              )
            })
          }

          <div className="mt-8 flex items-center gap-x-2">
            <IconCircleQuestion className="h-8 w-8 text-gray-400" aria-hidden="true" />
            <div>
              <div className="z-title1">{lang === "sv" ? "Saknar du något?" : "Anything missing?"}</div>
              <div
                className="z-body1">{lang === "sv" ? "Lägg till ditt eget hinder" : "Add your own barriers"}</div>
            </div>
          </div>

          {
            barriers.filter(b => b.custom).map((barrier, index) => {
              return (
                <div key={barrier.type + "_" + index}
                     className={selectCls}
                     onClick={() => checkBarrier(barrier.text)}>
                  <CheckBox checked={barrier.selected}/>
                  <div className="z-title2">{barrier.text}</div>
                </div>
              )
            })
          }

          <div className="mt-4 flex items-center gap-x-4">
            <input className="z-input" value={customText} onChange={(e) => updateCustom(e.target.value)}/>
            <CustomButton disabled={customDisabled()} buttonType="neutralBase" style={{width: "unset"}} onClick={() => addBarrier()}>
              {t("add")}
            </CustomButton>
          </div>

          {
            tooLong &&
              <div className="mt-2 text-sm text-cherry-500">{lang === "sv" ? "Max 40 tecken" : "Max 40 characters"}</div>
          }

          <div className={noBarrierCls}
               onClick={() => setNoBarriers(!noBarriers)}>
            <CheckBox checked={noBarriers}/>
            <div className="z-title2">{lang === "sv" ? "Jag har inga hinder just nu" : "I don't have any barriers right now"}</div>
          </div>

        </>

      )
    } else if (step === 2) {
      return (
        <div className="bg-indigo-800 rounded-md p-4">
          <div className="rounded-md border border-aquamarine-500 p-4 flex items-center gap-x-2">
            <CheckCircleIcon variant="filled" className="h-6 w-6 text-success-500!" aria-hidden="true"/>
            <div className="z-title3">{lang === "sv" ? "Genomgång klar" : "Barrier check completed"}</div>
          </div>
          <div className="mt-4 z-title1">
            {lang === "sv" ? "Dina identifierade hinder" : "Your identified barriers"}
          </div>

          {
            barrierChecked() ? (
              <>
                {
                  barriers.filter(b => b.selected).map((barrier, index) => (
                    <div key={"barrier_" + index}>
                      <div className="mt-2 border border-gray-400 rounded-full py-1 px-4 z-body3 inline-block">
                        {barrier.text}
                      </div>
                    </div>
                  ))
                }
              </>
            ) : (
              <div className="z-body1">
                {lang === "sv" ? "Inga hinder! Det är ett bra tecken — det verkar som om inget för närvarande står i vägen för ditt långsiktiga mål." :
                  "No barriers! That’s a good sign — it looks like nothing is currently standing in the way of this goal."}
              </div>
            )
          }

          <div className="mt-4 z-title1">
            {lang === "sv" ? "Rekommenderat nästa steg" : "Recommended next step"}
          </div>

          {
            barrierChecked() ? (
              <div className="z-body1">
                {lang === "sv" ? "Skapa beteenden som hjälper dig att hantera dina identifierade hinder." :
                  "Work with identified barrier(s) by creating behaviours to resolve them."}
              </div>
            ) : (
              <div className="z-body1">
                {lang === "sv" ? "Fortsätt mot ditt mål — lägg till beteenden och aktiviteter, och kom tillbaka när något skaver." :
                  "Continue moving forward with this goal by creating necessary behaviours and activities, and come back any time if any friction comes up."}
              </div>
            )
          }
        </div>
      )
    }
    return <>Missing step {step}</>
  }

  const saveDisabled = () => {
    if (props.readOnly) return true;
    return !(noBarriers || barrierChecked());

  }

  const saveBarriers = () => {
    const _saveBarriers: Array<BarrierInterface> = [];
    for (const _barrier of barriers) {
      if (_barrier.selected) {
        _saveBarriers.push({
          text: _barrier.text,
          custom: _barrier.custom,
          used: false,
        })
      }
    }
    props.save(_saveBarriers);
    setStep(2);
  }

  const renderFooter = () => {
    if (step === 2) {
      return (
        <div className="flex gap-x-2">

          <CustomButton disabled={props.readOnly} buttonType="outlineWhiteBase" size="sm"
                        onClick={() => setStep(1)}>
            {t("restart")}
          </CustomButton>

          <CustomButton buttonType="blueBase" size="sm"
                        onClick={() => setShowModal(false)}>
            {t("done")}
          </CustomButton>

        </div>

      )

    } else {
      return (
        <CustomButton disabled={saveDisabled()} buttonType="blueBase" size="sm" onClick={() => saveBarriers()}>
            {t("submit")}
        </CustomButton>
      )
    }
  }

  return (
    <div className={"bg-indigo-800 mt-4 p-4 rounded-md hover:bg-indigo-700 border-indigo-700 border " + (props.disabled ? "opacity-50 pointer-events-none" : "")}>
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          {
            props.done ? (
              <div className="rounded-sm p-2 bg-success-500 w-11 text-center">
                <IconCheck className="h-6 w-6 text-neutral-900!" aria-hidden="true" />
              </div>
            ) : (
              <div className="rounded-sm p-2 bg-indigo-700 border border-indigo-600 w-11 z-title3 text-center">{props.stepNumber}</div>
            )
          }
          <div className="ml-3">
            <div className="z-title1">{t("barriers")}</div>
            <div className="z-body1">
              {lang === "sv" ? "Sätt fingret på vad som står i vägen" : "Pin down what's getting in your way"}
            </div>
          </div>
        </div>
        {
          !props.disabled &&
            <CustomButton buttonType={props.done ? "transparentBase" : "blueBase"} size="sm"
                          disabled={(props.readOnly && props.barriers.length === 0)}
                          rightIcon={props.done ? null : <IconRight className="size-4 text-neutral-900!" aria-hidden="true" />}
                          onClick={() => setShowModal(true)} style={{width: "unset"}}>
              {props.barriers.length > 0 ? lang === "sv" ? "Visa" : "View" : lang === "sv" ? "Starta" : "Start"}
            </CustomButton>
        }
      </div>
      {
        (props.barriers.length === 0 && !props.done && !props.disabled && previousActivated &&
          (now.getTime() - previousActivated.getTime() > 7 * 24 * 3600 * 1000)) &&
        <AttentionBox message={lang === "sv" ? "Identifiera dina hinder" : "Identify you barriers"}/>
      }

      {
        showModal &&
          <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
              <ModalHeader onClose={() => setShowModal(false)}>
                {lang === "sv" ? "Hitta hinder" : "Barrier check"}
              </ModalHeader>
              <ModalBody>
                <div style={{height: wide ? 532 : "100%"}} className="overflow-y-scroll">
                  {renderStep()}
                </div>
              </ModalBody>
              <ModalFooter>
                <div style={{minHeight: 38, width: "100%"}}>
                  {renderFooter()}
                </div>
              </ModalFooter>
          </Modal>
      }
    </div>
  )
}

export default ClientBarriers;
